import Organization, {
  ShowGeneratedToken,
  PaySomeMoney,
  UserProfile,
  PermissionRequestResultContainer,
} from '@signavio/workflow-organizations'
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router'
import {
  switchOrganization,
  useLoggedInSession,
  useOrganization,
  useUser,
} from '@signavio/effektif-api'

import { AdminRoute } from '../components'
import Analytics from '@signavio/effektif-reporting'
import Cases from '@signavio/workflow-cases'
import Processes from '@signavio/workflows-app'
import Search from '../search'
import Services from '@signavio/workflow-services'
import { SystemAdministration } from '@signavio/system-admin'
import { useDispatch } from 'react-redux'
import { useEffect } from 'react'
import {
  getACHComponent,
  updateBuiltInSupportContext,
} from '../components/global-header/builtInSupport'

function AuthenticatedPage() {
  useOrganizationSwitch()

  const match = useRouteMatch()
  const location = useLocation()
  const user = useUser()
  const organization = useOrganization()

  useEffect(() => {
    updateBuiltInSupportContext({
      achComponent: getACHComponent(location.pathname),
    })
  }, [location.pathname])

  return (
    <Switch>
      {user.systemAdmin && (
        <Route
          path={`${match.url}/admin/systemAdministration`}
          component={SystemAdministration}
        />
      )}

      <Route path={`${match.url}/buy/:license?`} component={PaySomeMoney} />

      {organization.licenseRequired && (
        <Redirect to={`/${organization.key}/buy`} />
      )}

      <Route path={`${match.url}/profile`} component={UserProfile} />

      <Route
        path={`${match.url}/public-api-tokens/:code`}
        component={ShowGeneratedToken}
      />

      <Route path={`${match.url}/processes`} component={Processes} />

      <Redirect
        from={`${match.url}/examples`}
        to={`${match.url}/processes/examples`}
      />

      <Redirect
        from={`${match.url}/process/:processId/start/form`}
        to={`${match.url}/cases/start-form/:processId`}
      />

      <Redirect
        from={`${match.url}/process/:processId/start`}
        to={`${match.url}/cases/start/:processId`}
      />

      <Redirect
        from={`${match.url}/process/:workflowId`}
        to={`${match.url}/processes/process/:workflowId`}
      />

      <Redirect
        from={`${match.url}/process`}
        to={`${match.url}/processes/create`}
      />

      <AdminRoute path={`${match.url}/organization`} component={Organization} />

      {/* Old direct tasks overview to new one in cases namespace */}
      <Redirect
        from={`${match.url}/tasks/:tab?`}
        to={`${match.url}/cases/tasks/:tab?`}
      />

      <AdminRoute path={`${match.url}/services`} component={Services} />

      <Route
        path={`${match.url}/search/:category?/:searchTerm?`}
        component={Search}
      />

      <Route path={`${match.url}/cases`} component={Cases} />

      {/* Old case and task URLs to new ones */}
      <Redirect
        from={`${match.url}/case/:caseId/task/:taskId`}
        to={`${match.url}/cases/case/:caseId/task/:taskId`}
      />

      <Redirect
        from={`${match.url}/case/:caseId`}
        to={`${match.url}/cases/case/:caseId`}
      />

      {/* Beta case and task URLs to new ones */}
      <Redirect
        from={`${match.url}/ncase/case/:caseId/task/:taskId`}
        to={`${match.url}/cases/case/:caseId/task/:taskId`}
      />

      <Redirect
        from={`${match.url}/ncase/case/:caseId`}
        to={`${match.url}/cases/case/:caseId`}
      />

      <Route path={`${match.url}/analytics`} component={Analytics} />

      <Route
        path={`${match.url}/permission-requests/:requestId/result`}
        component={PermissionRequestResultContainer}
      />

      <Redirect to={`${match.url}/cases/tasks${location.search}`} />
    </Switch>
  )
}

const useOrganizationSwitch = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { organization: organizationKey } = useParams()
  const { organizations } = useLoggedInSession()
  const organization = useOrganization()

  useEffect(() => {
    const newOrganization = organizations.find(
      ({ key }) => key === organizationKey
    )

    if (newOrganization && newOrganization.key !== organization.key) {
      dispatch(
        switchOrganization({
          currentPath: location.pathname,
          oldOrganization: organization,
          newOrganization,
        })
      )
    }
  }, [
    organization,
    location.pathname,
    dispatch,
    organizations,
    organizationKey,
  ])
}

export default AuthenticatedPage
